import React from 'react';
import { Select, Segmented, Alert, Button, Popconfirm } from 'antd';
import { Store, Layers, Trash2 } from 'lucide-react';
import { StatusTag } from '../../../common';

function SegmentViewToggle({ viewMode, setViewMode, channel }) {
    if (channel !== 'moderno') return null;

    return (
        <div className="shelf-card p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-surface-900">Vista de segmentación</div>
                    <p className="m-0 mt-0.5 text-sm text-surface-500">
                        {viewMode === 'default'
                            ? 'Productos que aplican a todos los pisos de venta del cliente.'
                            : 'Selecciona un piso de venta para darle su propia lista de productos. Reemplaza por completo la del cliente en ese piso.'}
                    </p>
                </div>
                <Segmented
                    value={viewMode}
                    onChange={setViewMode}
                    className="shrink-0"
                    options={[
                        { label: 'Por defecto (cliente)', value: 'default' },
                        { label: 'Excepciones por piso', value: 'exception' },
                    ]}
                />
            </div>
        </div>
    );
}

/**
 * Multi-select of pisos de venta. Pisos can be selected together only when they use the same
 * products (../storeSegmentation.js): once one is chosen, pisos with a different segmentation
 * are disabled and marked "Segmentación distinta".
 */
function StoreSelector({ stores, selectedStoreIds, onChange, onSelectAllMatching, segmentations = new Map(), loading, overrideStoreIds = new Set() }) {
    const withOverride = stores.filter((s) => overrideStoreIds.has(String(s._id)));
    const selected = new Set(selectedStoreIds);
    const selectionKey = selectedStoreIds.length ? segmentations.get(selectedStoreIds[0])?.key : null;
    const matchesSelection = (id) => !selectionKey || segmentations.get(String(id))?.key === selectionKey;
    const matchingCount = selectionKey ? stores.filter((s) => matchesSelection(s._id)).length : 0;

    const toggle = (id) => {
        const key = String(id);
        onChange(selected.has(key) ? selectedStoreIds.filter((x) => x !== key) : [...selectedStoreIds, key]);
    };

    return (
        <div className="shelf-card p-5">
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <Store className="text-[15px] text-surface-500" />
                <span className="text-[15px] font-semibold text-surface-900">Pisos de venta</span>
                <StatusTag tone={withOverride.length ? 'warning' : 'neutral'}>
                    {withOverride.length
                        ? `${withOverride.length} piso${withOverride.length === 1 ? '' : 's'} con excepción`
                        : 'Ningún piso tiene excepción'}
                </StatusTag>
            </div>
            <Select
                mode="multiple"
                allowClear
                showSearch
                loading={loading}
                className="w-full"
                placeholder="Selecciona uno o varios pisos de venta"
                value={selectedStoreIds}
                onChange={onChange}
                optionFilterProp="label"
                maxTagCount="responsive"
                options={stores.map((store) => {
                    const id = String(store._id);
                    return {
                        value: id,
                        label: store.name,
                        hasOverride: overrideStoreIds.has(id),
                        disabled: !selected.has(id) && !matchesSelection(id),
                    };
                })}
                optionRender={(opt) => (
                    <span className="flex items-center justify-between gap-2">
                        <span className="truncate">{opt.label}</span>
                        <span className="flex shrink-0 items-center gap-1">
                            {opt.data.disabled && <StatusTag tone="neutral">Segmentación distinta</StatusTag>}
                            {opt.data.hasOverride && <StatusTag tone="warning">Excepción</StatusTag>}
                        </span>
                    </span>
                )}
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-surface-500">
                <span>Puedes elegir varios pisos a la vez si usan exactamente los mismos productos.</span>
                {selectionKey && matchingCount > selectedStoreIds.length && (
                    <Button size="small" type="link" className="px-0" onClick={onSelectAllMatching}>
                        {`Seleccionar los ${matchingCount} pisos con esta segmentación`}
                    </Button>
                )}
            </div>
            {withOverride.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="mr-1 text-xs text-surface-500">Con excepción:</span>
                    {withOverride.map((s) => {
                        const id = String(s._id);
                        const isSelected = selected.has(id);
                        const selectable = isSelected || matchesSelection(id);
                        return (
                            <button
                                key={id}
                                type="button"
                                disabled={!selectable}
                                title={selectable ? undefined : 'Usa otros productos que los pisos seleccionados'}
                                onClick={() => toggle(id)}
                                className={`inline-flex h-6 items-center rounded-md border px-2 text-xs font-medium transition-colors ${
                                    isSelected
                                        ? 'cursor-pointer border-warning-solid bg-warning-bg text-warning-fg'
                                        : selectable
                                          ? 'cursor-pointer border-warning-border bg-warning-bg text-warning-fg hover:border-warning-solid'
                                          : 'cursor-not-allowed border-surface-200 bg-surface-50 text-surface-400'
                                }`}>
                                {s.name}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function SegmentStatusBanner({ viewMode, hasOverride, storeName, storeCount = 1, diff, onDelete, deleting }) {
    if (viewMode === 'default') {
        return (
            <Alert
                type="info"
                showIcon
                icon={<Layers />}
                message="Segmentación por defecto"
                description="Los cambios se aplicarán a todos los pisos de venta de este cliente, salvo los que tengan excepciones."
            />
        );
    }

    if (!storeName) return null;

    return (
        <Alert
            type={hasOverride ? 'warning' : 'success'}
            showIcon
            message={hasOverride ? `Excepción activa: ${storeName}` : `Sin excepción: ${storeName}`}
            description={
                <>
                    {storeCount > 1
                        ? hasOverride
                            ? 'Estos pisos usan la misma lista de productos propia. Guardar la actualizará en todos.'
                            : 'Estos pisos usan la segmentación del cliente. Guardar les creará a todos la misma lista propia.'
                        : hasOverride
                          ? 'Este piso usa su propia lista de productos en lugar de la del cliente. Guardar la actualizará.'
                          : 'Este piso usa la segmentación del cliente. Guardar creará su propia lista.'}
                    {diff && (diff.added || diff.removed) ? (
                        <div className="mt-1 tabular-nums">
                            Diferencia con el cliente: <span className="font-medium text-success-fg">+{diff.added}</span> /{' '}
                            <span className="font-medium text-danger-fg">−{diff.removed}</span>
                        </div>
                    ) : null}
                </>
            }
            action={
                hasOverride && onDelete ? (
                    <Popconfirm
                        title={storeCount > 1 ? `¿Eliminar la excepción de ${storeCount} pisos?` : '¿Eliminar la excepción?'}
                        description={storeCount > 1 ? 'Los pisos volverán a usar la segmentación del cliente.' : 'El piso volverá a usar la segmentación del cliente.'}
                        okText="Eliminar"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                        onConfirm={onDelete}>
                        <Button danger size="small" icon={<Trash2 />} loading={deleting}>
                            Eliminar excepción
                        </Button>
                    </Popconfirm>
                ) : null
            }
        />
    );
}

export { SegmentViewToggle, StoreSelector, SegmentStatusBanner };
